/**
 * "Danh mục đồ án" – every approved plan of Hà Nội in one searchable, sortable list
 * (public/data/doan.json merged with the live project list of the planning system).
 *  - plans digitised on the city's planning system: one click flies to the boundary and opens the
 *    information panel on the project's documents;
 *  - plans that only exist as a published decision: a card with the decision, the drawings published
 *    by the Ministry of Construction / Hanoi Urban Planning Institute and an approximate extent
 *    (bounds of the communes named in the decision) that can be shown on the map.
 */
import { $, $$, esc, icon, fmt, toast, isMobile, sleep, scrollWithin } from './ui.js';
import { bus } from './state.js';
import { act } from './telemetry.js';
import { HANOI_BBOX, PLAN_BY_NO } from './config.js';
import { tokens, highlight } from './core/text.js';
import { normCode } from './core/intent.js';
import { bboxContains } from './core/geo.js';
import * as panel from './panel.js';
import * as layers from './layers.js';
import * as tools from './tools.js';
import * as dock from './dock.js';
import {
  loadCatalog, loadLive, liveProject, liveReady, entries, catalogDoc, KINDS, KIND,
  titleOf, pickLabel, onMap, decisionText, scaleValue, dateValue, haystack, insidePoint,
} from './doan.js';

let pm = null;
let dlg = null;
const els = {};
const st = { q: '', status: 'all', kind: '', sort: '', dir: 1, sel: null, active: -1, view: 'list' };
let shown = [];
let approx = null; // entry whose approximate extent is drawn on the map

const STATUS = [['all', 'Tất cả'], ['map', 'Trên bản đồ'], ['dec', 'Chỉ có quyết định']];
const COLS = [['', 'Mã', 'c-code'], ['name', 'Đồ án', 'c-name'], ['scale', 'Tỷ lệ', 'c-scale'], ['area', 'Diện tích (ha)', 'c-area'], ['pop', 'Dân số', 'c-pop'], ['date', 'Phê duyệt', 'c-date']];
const SORTERS = {
  name: (e) => titleOf(e).toLocaleLowerCase('vi'),
  scale: (e) => scaleValue(e.scale),
  area: (e) => e.area,
  pop: (e) => e.pop,
  date: (e) => dateValue(e.date),
};

export function initCatalog(planMap) {
  pm = planMap;
  dlg = $('#catalog-modal');
  if (!dlg) return;
  dlg.innerHTML = `
    <header class="cat-head">
      <div class="cat-head-text">
        <span class="eyebrow">${icon('library-big')}Danh mục đồ án</span>
        <h2 id="catalog-title">Đồ án quy hoạch Hà Nội</h2>
        <p class="cat-count" id="cat-count">Đang tải danh mục…</p>
      </div>
      <button type="button" class="icon-btn ghost" data-close aria-label="Đóng">${icon('x')}</button>
    </header>
    <div class="cat-tools">
      <div class="cat-search">
        ${icon('search')}
        <input id="cat-q" type="text" inputmode="search" enterkeyhint="search" placeholder="Mã, tên đồ án, số quyết định, xã / phường…" autocomplete="off" autocapitalize="off" spellcheck="false" aria-label="Tìm đồ án" role="combobox" aria-expanded="true" aria-controls="cat-list" aria-autocomplete="list">
        <button type="button" class="icon-btn ghost sm" data-act="clear-q" aria-label="Xoá nội dung tìm" hidden>${icon('x')}</button>
      </div>
      <div class="seg cat-status" role="radiogroup" aria-label="Trạng thái số hoá" id="cat-status"></div>
    </div>
    <div class="cat-kinds" id="cat-kinds" role="group" aria-label="Loại quy hoạch"></div>
    <div class="cat-main" id="cat-main" data-view="list">
      <section class="cat-pane" aria-label="Danh sách đồ án">
        <div class="cat-cols" id="cat-cols"></div>
        <ul class="cat-list" id="cat-list" role="listbox" aria-label="Đồ án quy hoạch"></ul>
      </section>
      <aside class="cat-detail" id="cat-detail" aria-label="Thông tin đồ án" tabindex="-1"></aside>
    </div>`;
  els.q = $('#cat-q', dlg);
  els.clear = $('[data-act="clear-q"]', dlg);
  els.count = $('#cat-count', dlg);
  els.status = $('#cat-status', dlg);
  els.kinds = $('#cat-kinds', dlg);
  els.cols = $('#cat-cols', dlg);
  els.list = $('#cat-list', dlg);
  els.detail = $('#cat-detail', dlg);
  els.main = $('#cat-main', dlg);
  dlg.addEventListener('click', onClick);
  dlg.addEventListener('cancel', (e) => { if (isMobile() && st.view === 'detail') { e.preventDefault(); back(); } });
  els.q.addEventListener('input', () => { st.q = els.q.value; st.active = -1; els.clear.hidden = !st.q; renderList(); });
  els.q.addEventListener('keydown', onKey);
  els.list.addEventListener('keydown', onKey);
  bus.on('catalog', (o) => openCatalog(o || {}));
  // warm up quietly so the dialog opens instantly and search can offer undigitised plans
  const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1500));
  idle(() => { loadCatalog().catch(() => {}); }, { timeout: 4000 });
}

export const isCatalogOpen = () => !!dlg?.open;

/** Open the dialog; `id` selects (and shows the card of) an entry, `q` pre-fills the search. */
export async function openCatalog({ id, q } = {}) {
  if (!dlg) return;
  if (!dlg.open) act('catalog', id ? 'entry' : q ? 'query' : 'open');
  if (isMobile() && layers.isDrawerOpen()) layers.closeDrawer();
  if (dock.isTrayOpen()) dock.closeTray();
  if (q != null) { st.q = q; st.active = -1; }
  if (!dlg.open) dlg.showModal();
  els.q.value = st.q;
  els.clear.hidden = !st.q;
  if (!catalogDoc()) {
    els.list.innerHTML = `<li class="cat-empty">${icon('loader-circle', 'spin')}Đang tải danh mục đồ án…</li>`;
    renderDetail();
    try { await loadCatalog(); } catch (e) {
      els.count.textContent = 'Không tải được danh mục';
      els.list.innerHTML = `<li class="cat-empty">${icon('circle-alert')}Không tải được danh mục đồ án.<button type="button" class="btn sm subtle" data-act="retry">${icon('refresh-cw')}Thử lại</button></li>`;
      return;
    }
    if (!dlg.open) return;
  }
  // the live list adds the boundaries (and any plan digitised after the catalogue was built)
  if (!liveReady()) loadLive().then(() => { if (dlg.open) renderList(); }).catch(() => {});
  if (id) {
    const e = entries().find((x) => x.id === id);
    if (e) {
      if (!visible(e)) { st.q = ''; st.status = 'all'; st.kind = ''; els.q.value = ''; els.clear.hidden = true; }
      st.sel = e;
      st.view = 'detail';
    }
  } else if (isMobile()) st.view = 'list';
  renderAll();
  if (id) scrollWithin(els.list, $(`.cat-item[aria-selected="true"]`, els.list), { block: 'center' });
  if (isMobile()) (st.view === 'detail' ? els.detail : dlg).focus({ preventScroll: true });
  else els.q.focus({ preventScroll: true });
}

export function closeCatalog() { if (dlg?.open) dlg.close(); }

/* ---------------- filtering ---------------- */

function queryOf() {
  const qt = tokens(st.q);
  return { qt, qc: normCode(st.q) };
}
function matches(e, { qt, qc }) {
  if (!qt.length) return true;
  const h = ' ' + haystack(e).replace(/[·,()]/g, ' ');
  if (qc.length >= 2 && e._code && e._code === qc) return true;
  return qt.every((t) => h.includes(' ' + t) || (t.length >= 3 && h.includes(t)));
}
/** Relevance inside a kind group: exact code, code prefix, title words, anything else. */
function score(e, { qt, qc }) {
  if (!qt.length) return 0;
  haystack(e);
  if (qc && e._code === qc) return 3;
  if (qc.length >= 2 && e._code && e._code.startsWith(qc)) return 2;
  const t = ' ' + tokens(titleOf(e)).join(' ');
  return qt.every((x) => t.includes(' ' + x)) ? 1 : 0;
}
const statusOk = (e, s = st.status) => s === 'all' || (s === 'map' ? onMap(e) : !onMap(e));
function visible(e) {
  const q = queryOf();
  return matches(e, q) && (!st.kind || e.kind === st.kind) && statusOk(e);
}

/* ---------------- rendering ---------------- */

function renderAll() { renderList(); renderDetail(); }

function renderList() {
  const all = entries();
  const q = queryOf();
  const byQ = all.filter((e) => matches(e, q));
  const byKind = byQ.filter((e) => !st.kind || e.kind === st.kind);
  const nMap = byKind.filter(onMap).length;
  const total = all.length, totalMap = all.filter(onMap).length;
  els.count.innerHTML = total
    ? `<b>${fmt.num(total)}</b> đồ án · <b>${fmt.num(totalMap)}</b> có trên bản đồ · <b>${fmt.num(total - totalMap)}</b> chỉ có quyết định`
    : 'Danh mục trống';
  const cnt = { all: byKind.length, map: nMap, dec: byKind.length - nMap };
  els.status.innerHTML = STATUS.map(([id, label]) => `<button type="button" role="radio" data-status="${id}" aria-checked="${st.status === id}">${esc(label)}<span class="n">${fmt.num(cnt[id])}</span></button>`).join('');
  const kc = {};
  for (const e of byQ) if (statusOk(e)) kc[e.kind] = (kc[e.kind] || 0) + 1;
  const nAll = byQ.filter((e) => statusOk(e)).length;
  els.kinds.innerHTML = `<button type="button" class="chip-toggle" data-kind="" aria-pressed="${!st.kind}">Mọi loại<span class="n">${fmt.num(nAll)}</span></button>`
    + KINDS.filter((k) => kc[k.id] || st.kind === k.id).map((k) => `<button type="button" class="chip-toggle" data-kind="${k.id}" aria-pressed="${st.kind === k.id}">${icon(k.icon)}${esc(k.short)}<span class="n">${fmt.num(kc[k.id] || 0)}</span></button>`).join('');
  els.cols.innerHTML = COLS.map(([id, label, cls]) => {
    const on = st.sort === id;
    const aria = on ? (id === '' ? 'none' : st.dir > 0 ? 'ascending' : 'descending') : 'none';
    return `<button type="button" class="cat-col ${cls}${on ? ' on' : ''}" data-sort="${id}" aria-sort="${aria}" ${id === '' ? 'data-tip="Thứ tự mặc định: theo loại, mã đồ án"' : ''}>${esc(label)}${on && id !== '' ? icon(st.dir > 0 ? 'chevron-up' : 'chevron-down') : ''}</button>`;
  }).join('') + '<span class="cat-col c-more" aria-hidden="true"></span>';

  shown = byKind.filter((e) => statusOk(e));
  let html = '';
  if (st.sort && SORTERS[st.sort]) {
    const key = SORTERS[st.sort];
    const dir = st.dir;
    shown = shown.map((e, i) => ({ e, i, v: key(e) })).sort((a, b) => {
      const na = typeof a.v === 'number' ? !Number.isFinite(a.v) : !a.v;
      const nb = typeof b.v === 'number' ? !Number.isFinite(b.v) : !b.v;
      if (na !== nb) return na ? 1 : -1; // blanks last in both directions
      if (na) return a.i - b.i;
      const c = typeof a.v === 'string' ? a.v.localeCompare(b.v, 'vi', { numeric: true }) : a.v - b.v;
      return c ? c * dir : a.i - b.i;
    }).map((x) => x.e);
    html = shown.map((e, i) => rowHtml(e, i)).join('');
  } else {
    // grouped by kind (catalogue order), most relevant first inside each group while searching
    const groups = KINDS.map((k) => ({ k, list: shown.filter((e) => e.kind === k.id) })).filter((g) => g.list.length);
    if (q.qt.length) for (const g of groups) g.list = g.list.map((e, i) => ({ e, i, s: score(e, q) })).sort((a, b) => b.s - a.s || a.i - b.i).map((x) => x.e);
    shown = groups.flatMap((g) => g.list);
    let i = 0;
    for (const g of groups) {
      const n = g.list.filter(onMap).length;
      html += `<li class="cat-group" role="presentation"><span>${icon(g.k.icon)}${esc(g.k.title)}</span><span class="n">${fmt.num(g.list.length)} đồ án${n && n < g.list.length ? ` · ${fmt.num(n)} trên bản đồ` : ''}</span></li>`;
      html += g.list.map((e) => rowHtml(e, i++)).join('');
    }
  }
  if (!shown.length) {
    const filtered = st.kind || st.status !== 'all';
    // the query does match, only the kind / status filter hides it: offer to drop the filter, keep the query
    const btn = filtered && byQ.length
      ? `<button type="button" class="btn sm subtle" data-act="reset-filters">${icon('rotate-ccw')}Bỏ bộ lọc, xem ${fmt.num(byQ.length)} đồ án khớp</button>`
      : st.q || filtered ? `<button type="button" class="btn sm subtle" data-act="reset">${icon('rotate-ccw')}Xoá tìm kiếm, bộ lọc</button>` : '';
    html = `<li class="cat-empty">${icon('search')}<span>Không có đồ án nào khớp${st.q ? ` “${esc(st.q)}”` : ''}${filtered ? ' với bộ lọc đang chọn' : ''}.</span>${btn}</li>`;
  }
  els.list.innerHTML = html;
  if (st.active >= shown.length) st.active = shown.length - 1;
  syncActive(false);
}

function statusText(e) { return e.layer ? 'Lớp bản đồ' : e.digitized ? 'Trên bản đồ' : 'Chỉ có quyết định'; }
const num = (v, d = 0) => (Number.isFinite(v) ? fmt.num(v, d) : '<span class="nil">—</span>');

function rowHtml(e, i) {
  const k = KIND[e.kind] || KIND.qhpk;
  const title = titleOf(e);
  const on = onMap(e);
  const sub = [e.dec, e.addr].filter(Boolean).join(' · ');
  return `<li class="cat-item${on ? ' on' : ''}" role="option" id="cat-opt-${i}" data-i="${i}" aria-selected="${st.sel === e}">
    <button type="button" class="cat-row" data-act="pick" data-i="${i}" tabindex="-1" aria-label="${esc(title)}${on ? ' – bay tới ranh giới' : ' – xem thông tin quyết định'}">
      <span class="cat-c c-code">${e.code ? `<span class="cat-codebox">${esc(e.code)}</span>` : `<span class="cat-codebox ico">${icon(k.icon)}</span>`}</span>
      <span class="cat-c c-name"><b>${highlight(title, st.q, esc)}</b><small><span class="cat-st ${on ? 'ok' : 'dec'}">${statusText(e)}</span>${sub ? `<span class="cat-sub">${esc(sub)}</span>` : ''}</small></span>
      <span class="cat-c c-scale">${e.scale ? esc(e.scale) : '<span class="nil">—</span>'}</span>
      <span class="cat-c c-area">${num(e.area, e.area >= 100 ? 0 : 1)}</span>
      <span class="cat-c c-pop">${num(e.pop)}</span>
      <span class="cat-c c-date">${e.date ? esc(e.date) : '<span class="nil">—</span>'}</span>
    </button>
    ${on
    ? `<button type="button" class="icon-btn ghost sm cat-more" data-act="detail" data-i="${i}" tabindex="-1" aria-label="Thông tin ${esc(title)}" data-tip="Xem thông tin">${icon('info')}</button>`
    : `<span class="cat-more" aria-hidden="true">${icon('chevron-right')}</span>`}
  </li>`;
}

function renderDetail(instant = false) {
  const e = st.sel;
  els.detail.classList.toggle('instant', instant); // keyboard preview: no fade on every arrow press
  els.main.dataset.view = dlg.dataset.view = e && st.view === 'detail' ? 'detail' : 'list';
  els.detail.innerHTML = e ? cardHtml(e) : introHtml();
  els.detail.scrollTop = 0;
}

function introHtml() {
  const doc = catalogDoc();
  const all = entries();
  const stats = KINDS.map((k) => {
    const list = all.filter((e) => e.kind === k.id);
    if (!list.length) return '';
    const n = list.filter(onMap).length;
    return `<button type="button" class="cat-stat" data-kind="${k.id}"><span class="cat-codebox ico">${icon(k.icon)}</span><span class="grow"><b>${esc(k.title)}</b><small>${n ? `${fmt.num(n)} trên bản đồ` : 'Chưa số hoá'}${n < list.length && n ? ` · ${fmt.num(list.length - n)} chỉ có quyết định` : ''}</small></span><span class="n">${fmt.num(list.length)}</span></button>`;
  }).join('');
  const src = (doc?.sources || []).map((s) => `<a href="${esc(s.url)}" target="_blank" rel="noopener">${esc(s.name)}</a>`).join(' · ');
  return `<div class="cat-intro">
    <div class="cat-legend">
      <div><span class="cat-st ok">Trên bản đồ</span><span>Đã số hoá trên Hệ thống thông tin quy hoạch TP Hà Nội: bấm để bay tới ranh giới, xem chức năng, chỉ tiêu từng ô đất.</span></div>
      <div><span class="cat-st dec">Chỉ có quyết định</span><span>Chưa có ranh giới số hoá: xem quyết định phê duyệt, bản vẽ đã công bố và vị trí gần đúng.</span></div>
    </div>
    ${stats ? `<div class="cat-stats">${stats}</div>` : ''}
    <p class="cat-src">${icon('database')}<span>Nguồn: ${src || 'Hệ thống thông tin quy hoạch TP Hà Nội'}${doc?.built ? ` · cập nhật ${esc(doc.built.split('-').reverse().join('/'))}` : ''}. Thông tin chỉ có giá trị tham khảo.</span></p>
  </div>`;
}

const LINK_ICON = { pdf: 'file-text', img: 'file-image', tif: 'file-image', page: 'globe' };
const LINK_TYPE = { pdf: 'PDF', img: 'Ảnh', tif: 'TIFF, dung lượng lớn', page: 'Trang web' };

function actionOf(e) {
  if (e.layer) return { label: `Bật lớp ${PLAN_BY_NO[e.layer]?.short || 'trên bản đồ'}`, icon: 'layers' };
  if (e.digitized) return { label: 'Bay tới ranh giới', icon: 'scan-search' };
  if (e.approx) return { label: 'Xem vị trí gần đúng', icon: 'map-pin' };
  if (e.bbox) return { label: 'Xem phạm vi trên bản đồ', icon: 'map' };
  return null;
}

function cardHtml(e) {
  const k = KIND[e.kind] || KIND.qhpk;
  const title = titleOf(e);
  const on = onMap(e);
  const badges = [];
  if (e.code) badges.push(`<span class="badge code" data-tip="Mã đồ án">${icon('hash')}${esc(e.code)}</span>`);
  badges.push(e.layer ? `<span class="badge accent">${icon('layers')}Lớp bản đồ</span>`
    : e.digitized ? `<span class="badge ok">${icon('vector-square')}Có ranh giới trên bản đồ</span>`
      : `<span class="badge warn">${icon('scroll-text')}Chỉ có quyết định</span>`);
  if (e.scale) badges.push(`<span class="badge">Tỷ lệ ${esc(e.scale)}</span>`);
  const rows = [];
  const add = (key, v) => { if (v != null && v !== '') rows.push([key, v]); };
  add('Loại quy hoạch', k.title);
  add('Quyết định phê duyệt', decisionText(e));
  add('Cơ quan phê duyệt', e.approver);
  add('Diện tích', Number.isFinite(e.area) ? `${fmt.num(e.area, 2)} ha` : null);
  add('Quy mô dân số', Number.isFinite(e.pop) ? `${fmt.num(e.pop)} người${e.popYear ? ` (đến năm ${e.popYear})` : ''}` : null);
  add('Phạm vi', e.addr);
  const act = actionOf(e);
  const pdf = (e.links || []).find((l) => l.kind === 'pdf');
  const links = (e.links || []).map((l) => `<a class="link-row" href="${esc(l.url)}" target="_blank" rel="noopener">${icon(LINK_ICON[l.kind] || 'file')}<span class="grow">${esc(l.label)}<small>${esc([l.src, LINK_TYPE[l.kind]].filter(Boolean).join(' · '))}</small></span>${icon('external-link', 'sm')}</a>`).join('');
  const notes = [];
  if (e.note) notes.push(`<div class="note">${icon('info')}<div>${esc(e.note)}</div></div>`);
  if (!e.digitized && !e.layer) {
    notes.push(`<div class="note warn">${icon('triangle-alert')}<div>Đồ án chưa có ranh giới số hoá trên Hệ thống thông tin quy hoạch TP Hà Nội nên không tra cứu được từng ô đất.${e.approx ? ' Vị trí trên bản đồ là phạm vi gần đúng, ước tính từ địa giới các xã, phường nêu trong quyết định (trước khi sắp xếp đơn vị hành chính năm 2025).' : ''}</div></div>`);
  }
  return `<article class="cat-card" aria-labelledby="cat-card-title">
    <button type="button" class="cat-back" data-act="back">${icon('arrow-left')}Danh sách</button>
    <div class="cat-card-top">
      <span class="card-ico">${icon(k.icon)}</span>
      <div class="cat-card-text">
        <span class="cat-kind">${esc(k.title)}</span>
        <h3 id="cat-card-title">${esc(title)}</h3>
        ${fold2(e.name) !== fold2(title) ? `<p class="cat-full">${esc(e.name)}</p>` : ''}
      </div>
    </div>
    <div class="badges">${badges.join('')}</div>
    ${act || pdf ? `<div class="cat-actions">
      ${act ? `<button type="button" class="btn ${on || e.approx || e.bbox ? 'primary' : 'subtle'}" data-act="go">${icon(act.icon)}${esc(act.label)}</button>` : ''}
      ${pdf ? `<a class="btn subtle" href="${esc(pdf.url)}" target="_blank" rel="noopener">${icon('file-text')}Mở quyết định</a>` : ''}
    </div>` : ''}
    ${rows.length ? `<div class="kv">${rows.map(([a, b]) => `<div>${esc(a)}</div><div>${esc(b)}</div>`).join('')}</div>` : ''}
    ${notes.join('')}
    ${links ? `<div class="section-title"><span>Văn bản, bản vẽ công bố (${e.links.length})</span></div><div class="cat-links">${links}</div>`
    : `<div class="empty-line">${icon('file')}${e.digitized ? 'Hồ sơ đính kèm xem ở tab “Đồ án” của bảng thông tin sau khi bay tới ranh giới.' : 'Chưa tìm thấy văn bản công bố trực tuyến.'}</div>`}
  </article>`;
}
const fold2 = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

/* ---------------- interaction ---------------- */

function onClick(ev) {
  if (ev.target === dlg || ev.target.closest('[data-close]')) { dlg.close(); return; }
  const t = ev.target.closest('[data-act],[data-sort],[data-status],[data-kind]');
  if (!t) return;
  if (t.dataset.status != null) { st.status = t.dataset.status; st.active = -1; renderList(); return; }
  if (t.dataset.kind != null) {
    st.kind = t.closest('.cat-stat') ? t.dataset.kind : st.kind === t.dataset.kind ? '' : t.dataset.kind;
    st.active = -1;
    renderList();
    if (t.closest('.cat-stat') && isMobile()) back();
    return;
  }
  if (t.dataset.sort != null) {
    const id = t.dataset.sort;
    if (id === '') { st.sort = ''; st.dir = 1; } else if (st.sort === id) st.dir = -st.dir;
    else { st.sort = id; st.dir = id === 'area' || id === 'pop' || id === 'date' ? -1 : 1; }
    renderList();
    return;
  }
  const e = t.dataset.i != null ? shown[+t.dataset.i] : st.sel;
  switch (t.dataset.act) {
    case 'pick': if (!e) break; st.active = +t.dataset.i; if (onMap(e)) showEntry(e); else select(e); break;
    case 'detail': if (e) { st.active = +t.dataset.i; select(e); } break;
    case 'go': if (st.sel) showEntry(st.sel); break;
    case 'back': back(); break;
    case 'clear-q': st.q = ''; els.q.value = ''; els.clear.hidden = true; renderList(); els.q.focus(); break;
    case 'reset': st.q = ''; st.kind = ''; st.status = 'all'; els.q.value = ''; els.clear.hidden = true; renderList(); els.q.focus(); break;
    case 'reset-filters': st.kind = ''; st.status = 'all'; renderList(); els.q.focus(); break;
    case 'retry': openCatalog({}); break;
    default:
  }
}

function select(e) {
  st.sel = e;
  st.view = 'detail';
  for (const li of $$('.cat-item', els.list)) li.setAttribute('aria-selected', String(shown[+li.dataset.i] === e));
  renderDetail();
  if (isMobile()) els.detail.focus({ preventScroll: true });
}

function back() {
  st.view = 'list';
  renderDetail();
  const li = st.sel && $(`.cat-item[aria-selected="true"]`, els.list);
  scrollWithin(els.list, li, { margin: 4 });
}

function onKey(e) {
  if (!shown.length) return;
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault();
    const n = shown.length;
    st.active = st.active < 0 ? (e.key === 'ArrowDown' ? 0 : n - 1) : (st.active + (e.key === 'ArrowDown' ? 1 : n - 1)) % n;
    syncActive(true);
    if (!isMobile()) { st.sel = shown[st.active]; st.view = 'detail'; for (const li of $$('.cat-item', els.list)) li.setAttribute('aria-selected', String(+li.dataset.i === st.active)); renderDetail(true); }
  } else if (e.key === 'Enter' && e.target === els.q) {
    e.preventDefault();
    const it = shown[st.active >= 0 ? st.active : 0];
    if (!it) return;
    // digitised: fly at once; decision only: first Enter shows the card, the next one the map
    if (onMap(it) || (st.sel === it && st.view === 'detail' && actionOf(it))) showEntry(it);
    else { st.active = shown.indexOf(it); syncActive(true); select(it); if (!isMobile()) els.q.focus({ preventScroll: true }); }
  }
}

function syncActive(scroll) {
  for (const li of $$('.cat-item', els.list)) li.classList.toggle('active', +li.dataset.i === st.active);
  if (st.active >= 0) {
    els.q.setAttribute('aria-activedescendant', 'cat-opt-' + st.active);
    if (scroll) scrollWithin(els.list, $(`#cat-opt-${st.active}`, els.list), { margin: 4 });
  } else els.q.removeAttribute('aria-activedescendant');
}

/* ---------------- map actions ---------------- */

/**
 * Show an entry on the map: digitised plans fly to their boundary and open the information panel
 * on the project; plan layers are switched on; decision-only plans get their approximate extent.
 * Also used by the search box for catalogue results.
 */
export async function showEntry(e) {
  if (!e || !pm) return;
  if (dlg?.open) dlg.close();
  await pm.ready;
  if (tools.capturing()) tools.stop();
  if (e.layer) {
    clearApprox();
    layers.setPlan(e.layer, { announce: true });
    pm.fitBBox(e.bbox || HANOI_BBOX, { padding: panel.viewPadding(), maxZoom: 12 });
    return;
  }
  if (e.digitized) {
    clearApprox();
    if (!liveReady()) {
      const t = setTimeout(() => toast('Đang tải ranh giới đồ án…', { type: 'info', ms: 2200 }), 350);
      await Promise.race([loadLive().catch(() => null), sleep(8000)]);
      clearTimeout(t);
    }
    const p = liveProject(e);
    const pt = insidePoint(e);
    if (!pt) return;
    const label = pickLabel(e);
    panel.openAt({ lat: pt[1], lng: pt[0] }, { label, sub: e.name !== label ? e.name : KIND[e.kind]?.title, kind: 'search', fly: false, project: p, tab: 'docs', focusProject: e.pid });
    const bb = p?.bbox || e.bbox;
    if (bb) pm.fitBBox(bb, { padding: panel.viewPadding(), maxZoom: 16 });
    return;
  }
  // decision only: approximate extent (or the whole city), never presented as a boundary
  if (panel.isOpen()) await panel.closePanel();
  if (e.approx && e.bbox) {
    approx = e;
    pm.setApprox(e.bbox, { title: pickLabel(e), sub: 'Vị trí gần đúng · chỉ có quyết định' });
  } else clearApprox();
  if (e.bbox) pm.fitBBox(e.bbox, { padding: panel.viewPadding(), maxZoom: e.approx ? 15 : 12 });
  toast(e.approx ? `${pickLabel(e)}: vị trí gần đúng, đồ án chưa có ranh giới số hoá` : `${pickLabel(e)}: đồ án chưa có ranh giới số hoá`, {
    type: 'info', ms: 8000, action: { label: 'Xem quyết định', onClick: () => openCatalog({ id: e.id }) },
  });
}

/** Approximate extent shown? (Esc / a click elsewhere clears it.) */
export const hasApprox = () => !!approx;
export function clearApprox() {
  if (!approx) return;
  approx = null;
  pm?.setApprox(null);
}
/** Map click: keep the extent while exploring inside it, drop it when the user moves on. */
export function onMapClick(ll) {
  if (approx && !bboxContains(approx.bbox, ll.lng, ll.lat)) clearApprox();
}
